import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import WalletController from "../../../controller/walletController";
import {
  getHomePageInfos,
  createPayByCardOperation,
  createPayByCardHostedPayment,
  ensureLocation,
} from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { FeesFor } from "../../../utils/enums";
import { useToast } from "../../../contexts/ToastContext";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import {
  getPermissionControllers,
  hasCustomerPermission,
  isAccountApprovalLocked,
} from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import WalletCurrencyFlag from "../../../components/WalletCurrencyFlag";
import {
  formatCashWalletDropdownLabel,
  resolveCashWalletCurrencyDisplay,
} from "../../../utils/currencyFlagHelper";
import "../../css/cachIn.css";

function parseWalletCurrencyRange(walletData) {
  const raw = walletData?.currencies?.RANGE ?? walletData?.RANGE;
  if (raw == null) return { min: null, max: null };
  try {
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    return {
      min: r.MIN != null ? parseFloat(r.MIN) : null,
      max: r.MAX != null ? parseFloat(r.MAX) : null,
    };
  } catch (_) {
    return { min: null, max: null };
  }
}

/**
 * Applies optional currency restriction for PAY_BY_CARD (matches wallet restriction FOR fees type).
 */
function effectiveMinAmount(walletData) {
  const { min } = parseWalletCurrencyRange(walletData);
  let best = Number.isFinite(min) ? min : null;
  const restrictions =
    walletData?.currencies?.restriction || walletData?.restriction || [];
  if (!Array.isArray(restrictions) || restrictions.length === 0) return best;

  restrictions.forEach((row) => {
    if (
      row &&
      Number(row.FOR) === Number(FeesFor.PAY_BY_CARD) &&
      row.MIN_AMOUNT != null
    ) {
      const v = parseFloat(row.MIN_AMOUNT);
      if (!Number.isNaN(v)) {
        if (best == null || v > best) best = v;
      }
    }
  });
  return best;
}

/** Maps axios/network failures to clearer copy without changing the API (e.g. 405 / Laravel method mismatch). */
function messageForPayByCardAxiosError(err, t) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  let bodySnippet = "";
  if (typeof data === "string") bodySnippet = data;
  else if (data != null && typeof data === "object" && data.message != null) {
    bodySnippet = String(data.message);
  }
  const mentionsPostMethod =
    typeof bodySnippet === "string" &&
    bodySnippet.includes("POST method is not supported");
  if (status === 405 || mentionsPostMethod) {
    return t("pay_with_card_api_405_hint", { ns: "mobile" });
  }
  return err?.message || t("payment_failed", { ns: "mobile" });
}

export default function PayWithCard() {
  const { t } = useTranslation();
  const { success: showSuccess, error: showError, warning: showWarning } =
    useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordDialogData, setPasswordDialogData] = useState(null);

  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { customerPerm, customerDevicePerm } = permissions;

  const canDepositByCard =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_DEPOSIT_BY_CARD
    );

  const refreshWallets = useCallback(async () => {
    try {
      const ewData = await WalletController.fetchEWalletsWithAmounts();
      const latest = Array.isArray(ewData?.wallets) ? ewData.wallets : [];
      setWallets(
        latest.map((w) => {
          const balance =
            w.CURRENT_BALENCE ||
            w.CURRENT_BALANCE ||
            w.BALANCE ||
            w.AMOUNT ||
            0;
          return {
            value: w.ID ?? w.id,
            label: formatCashWalletDropdownLabel(w, balance),
            data: w,
          };
        })
      );
    } catch (_) {
      setWallets([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        try {
          const stored = sessionStorage.getItem("userData");
          if (stored) setUser(JSON.parse(stored));
        } catch (_) {}

        try {
          const userResponse = await getHomePageInfos();
          const userData = userResponse?.decryptedData || userResponse?.data;
          if (
            userData &&
            userData.CODE === AppConstants.responseOk &&
            userData.DATA
          ) {
            setUser(userData.DATA);
            try {
              sessionStorage.setItem("userData", JSON.stringify(userData.DATA));
              sessionStorage.setItem("userDataTimestamp", String(Date.now()));
            } catch (_) {}
          }
        } catch (_) {}

        await refreshWallets();
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [refreshWallets]);

  useEffect(() => {
    const onFundsRefresh = () => {
      refreshWallets();
    };
    window.addEventListener("mfg:funds-refresh", onFundsRefresh);
    return () => window.removeEventListener("mfg:funds-refresh", onFundsRefresh);
  }, [refreshWallets]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const payResult = sp.get("payResult");
    if (!payResult) return;
    navigate("/pages/pay-with-card", { replace: true });
    if (payResult === "ok") {
      showSuccess(
        t("successfully_operation", { ns: "mobile" }),
        t("payment_done", { ns: "mobile" })
      );
    } else if (payResult === "error" || payResult === "d" || payResult === "e") {
      showError(
        t("error_operation", { ns: "mobile" }),
        t("payment_failed", { ns: "mobile" })
      );
    } else {
      showWarning(
        t("error_operation", { ns: "mobile" }),
        t("pending_payment", { ns: "mobile" })
      );
    }
    refreshWallets();
    try {
      window.dispatchEvent(new Event("mfg:funds-refresh"));
    } catch (_) {}
  }, [location.search, navigate, t, showSuccess, showError, showWarning, refreshWallets]);

  const selectedWalletData = useMemo(() => {
    const v = typeof wallet === "object" ? wallet?.value : wallet;
    return wallets.find((w) => w.value === v)?.data ?? null;
  }, [wallet, wallets]);

  const walletSymbol = selectedWalletData
    ? resolveCashWalletCurrencyDisplay(selectedWalletData).symbol ||
      selectedWalletData.currencies?.CURRENCY_SYMBOL ||
      ""
    : "";

  const parseAmountNum = () => {
    const n = parseFloat(String(amount || "").trim().replace(/,/g, ""));
    return Number.isFinite(n) ? n : NaN;
  };

  const handleProceed = async () => {
    if (!canDepositByCard) return;

    const newErrors = {};
    const loc = await ensureLocation();
    if (!loc.ok) {
      const msg =
        loc.error === "location_denied"
          ? t("location_permission_denied_funds", { ns: "mobile" })
          : loc.error === "location_unavailable"
            ? t("location_unavailable_funds", { ns: "mobile" })
            : t("location_required_funds", { ns: "mobile" });
      showError(t("error_operation", { ns: "mobile" }), msg);
      return;
    }

    if (!selectedWalletData) newErrors.wallet = t("please_select_wallet", { ns: "mobile" });

    const amountValue = parseAmountNum();
    if (!amount || Number.isNaN(amountValue) || amountValue <= 0) {
      newErrors.amount = t("please_enter_amount", { ns: "mobile" });
    } else {
      const minAmt = effectiveMinAmount(selectedWalletData);
      const { min } = parseWalletCurrencyRange(selectedWalletData);
      const backendMinUsed = Number.isFinite(minAmt) ? minAmt : min;
      if (Number.isFinite(backendMinUsed) && amountValue <= backendMinUsed) {
        newErrors.amount = `${t("min_amount_to_send_is", { ns: "mobile" })} ${backendMinUsed}`;
      }
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const walletId =
      typeof wallet === "object" ? wallet?.value : wallet ?? selectedWalletData?.ID;

    try {
      setProcessing(true);
      const res = await createPayByCardOperation({
        walletId,
        amount: amountValue,
      });
      const d = res.decryptedData || res.data || {};
      const code = Number(d.CODE ?? d.code ?? 0);

      if (code === AppConstants.responseOk || code === 200) {
        const txId =
          d.TRANSACTION_ID != null ? String(d.TRANSACTION_ID) : "";
        const feeRaw = d.FEES;
        const feeNum =
          typeof feeRaw === "number" ? feeRaw : parseFloat(feeRaw) || 0;
        const feeStr = Number.isFinite(feeNum)
          ? feeNum.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : String(feeRaw ?? "");

        setPasswordDialogData({
          requestToken: txId,
          firstMessage: t("pay_with_card_confirm_line", {
            ns: "mobile",
            amount: amountValue.toLocaleString(),
            symbol: walletSymbol || "",
            fee: feeStr,
          }),
          secondMessage: "",
        });
        setPasswordDialogOpen(true);
      } else if (code === AppConstants.responseNeedPermission || code === 204) {
        showError(
          t("error_operation", { ns: "mobile" }),
          t("you_dont_have_access_to_do_this_operation", { ns: "mobile" })
        );
      } else if (code === AppConstants.responseFailed || code === 207) {
        showError(
          t("error_operation", { ns: "mobile" }),
          d.MESSAGE ||
            d.message ||
            t("payment_failed", { ns: "mobile" })
        );
      } else {
        showError(
          t("error_operation", { ns: "mobile" }),
          d.MESSAGE || d.message || t("payment_failed", { ns: "mobile" })
        );
      }
    } catch (e) {
      showError(
        t("error_operation", { ns: "mobile" }),
        messageForPayByCardAxiosError(e, t)
      );
    } finally {
      setProcessing(false);
    }
  };

  const handlePasswordDialogClose = async (status, extras = {}) => {
    const transactionId = extras?.transactionId ?? null;

    setPasswordDialogOpen(false);
    setPasswordDialogData(null);

    if (status === "ok") {
      if (!transactionId) {
        showError(
          t("error_operation", { ns: "mobile" }),
          t("payment_failed", { ns: "mobile" })
        );
        return;
      }
      try {
        setProcessing(true);
        const res = await createPayByCardHostedPayment(transactionId);
        const d = res.decryptedData || res.data || {};
        const code = Number(d.CODE ?? d.code ?? 0);
        const serverMsg =
          (typeof d.MESSAGE === "string" && d.MESSAGE.trim() !== "")
            ? d.MESSAGE.trim()
            : typeof d.message === "string"
              ? d.message.trim()
              : "";
        if (code === AppConstants.responseOk || code === 200) {
          const redirectUrl =
            d.REDIRECT_URL != null ? String(d.REDIRECT_URL).trim() : "";
          if (!redirectUrl) {
            showError(
              t("error_operation", { ns: "mobile" }),
              serverMsg || t("pay_with_card_hosted_error", { ns: "mobile" })
            );
            return;
          }
          window.location.assign(redirectUrl);
          return;
        }
        if (
          code === AppConstants.responseAlreadyOk ||
          code === 205
        ) {
          showError(
            t("error_operation", { ns: "mobile" }),
            serverMsg || t("pay_with_card_hosted_session_state", { ns: "mobile" })
          );
          return;
        }
        if (code === AppConstants.responseNeedPermission || code === 204) {
          showError(
            t("error_operation", { ns: "mobile" }),
            t("you_dont_have_access_to_do_this_operation", { ns: "mobile" })
          );
          return;
        }
        showError(
          t("error_operation", { ns: "mobile" }),
          serverMsg ||
            (code === AppConstants.catchError || code === 707
              ? t("pay_with_card_hosted_error", { ns: "mobile" })
              : t("payment_failed", { ns: "mobile" }))
        );
      } catch (e) {
        showError(
          t("error_operation", { ns: "mobile" }),
          messageForPayByCardAxiosError(e, t)
        );
      } finally {
        setProcessing(false);
      }
      return;
    } else if (status === "canceled") {
      showWarning(
        t("error_operation", { ns: "mobile" }),
        t("cash_in_popup_cancelled_notice", { ns: "mobile" })
      );
      setAmount("");
      setWallet(null);
    } else if (status === "failed" || status === "invalid_password") {
      const rd = extras.responseData || {};
      const msg =
        (typeof rd.MESSAGE === "string" && rd.MESSAGE.trim() !== ""
          ? rd.MESSAGE.trim()
          : "") ||
        (typeof rd.message === "string" ? rd.message.trim() : "");
      showError(
        t("error_operation", { ns: "mobile" }),
        msg || t("payment_failed", { ns: "mobile" })
      );
    }
  };

  const lockedPanel = approvalLocked || !canDepositByCard;

  return (
    <div className="cash-in-page">
      <Sidebar user={user} />
      <div className="cash-in-content">
        {loading && (
          <div className="cash-in-loading-message">
            {t("loading", { ns: "mobile" })}...
          </div>
        )}

        <div className="cash-in-container cash-in-container--styled">
          {lockedPanel ? (
            <div className="cash-in-tab-content cash-in-tab-content--styled">
              <div className="cash-in-locked-message">
                <i className="fa-solid fa-lock"></i>
                <p>
                  {approvalLocked
                    ? t("you_dont_have_access_to_do_this_operation", {
                        ns: "mobile",
                      })
                    : t("you_dont_have_access_to_do_this_operation", {
                        ns: "mobile",
                      })}
                </p>
              </div>
            </div>
          ) : (
            <div className="cash-in-tab-content cash-in-tab-content--styled">
              <div className="cash-in-layout-grid">
                <div className="cash-in-form-panel">
                  <div className="cash-in-panel-header">
                    <h2>{t("pay_with_card", { ns: "mobile" })}</h2>
                    <p>{t("pay_with_card_description", { ns: "mobile" })}</p>
                  </div>

                  <div className="cash-in-form">
                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">
                        {t("wallet", { ns: "mobile" })}
                      </label>
                      <CustomDropdown
                        options={wallets}
                        value={wallet}
                        onChange={setWallet}
                        placeholder={
                          wallets.length === 0
                            ? t("no_wallets", { ns: "mobile" })
                            : t("select_wallet", { ns: "mobile" })
                        }
                        className={`cash-in-dropdown ${errors.wallet ? "dropdown-error" : ""}`}
                        disabled={loading || wallets.length === 0}
                        getDisplayValue={(option) => {
                          if (!option) return "";
                          if (typeof option === "object" && option.data) {
                            const walletData = option.data;
                            const balance =
                              walletData.CURRENT_BALENCE ||
                              walletData.CURRENT_BALANCE ||
                              walletData.BALANCE ||
                              walletData.AMOUNT ||
                              0;
                            return formatCashWalletDropdownLabel(walletData, balance);
                          }
                          return typeof option === "object" ? option.label : option;
                        }}
                        renderOption={(option, isSelected) => {
                          const walletData = option?.data;
                          if (!walletData) {
                            return (
                              <div className="cash-in-wallet-option">
                                <span>{option?.label ?? ""}</span>
                                {isSelected && (
                                  <i className="fa-solid fa-check custom-dropdown-check"></i>
                                )}
                              </div>
                            );
                          }
                          const balance =
                            walletData.CURRENT_BALENCE ||
                            walletData.CURRENT_BALANCE ||
                            walletData.BALANCE ||
                            walletData.AMOUNT ||
                            0;
                          const balanceNum =
                            typeof balance === "string"
                              ? parseFloat(balance.replace(/,/g, ""))
                              : parseFloat(balance);
                          const formattedBalance = Number.isNaN(balanceNum) ? 0 : balanceNum;
                          const {
                            flagCode: flagCountryCode,
                            code: currencyCode,
                            sym: currencySymbol,
                            suffix,
                          } = resolveCashWalletCurrencyDisplay(
                            walletData,
                            typeof option === "object" ? option.label : ""
                          );
                          return (
                            <div
                              className="cash-in-wallet-option"
                              style={{ display: "flex", alignItems: "center", gap: 8 }}
                            >
                              <WalletCurrencyFlag
                                countryCode={flagCountryCode}
                                title={currencyCode || currencySymbol || flagCountryCode || ""}
                              />
                              <span>
                                {formattedBalance.toFixed(2)}
                                {suffix ? ` ${suffix}` : ""}
                              </span>
                              {isSelected && (
                                <i className="fa-solid fa-check custom-dropdown-check"></i>
                              )}
                            </div>
                          );
                        }}
                        renderValue={(option, placeholderText) => {
                          if (!option) {
                            return (
                              <span className="custom-dropdown-placeholder">
                                {placeholderText}
                              </span>
                            );
                          }
                          const walletData =
                            typeof option === "object" && option.data ? option.data : null;
                          if (!walletData) {
                            return (
                              <span>
                                {typeof option === "object" ? option.label : option}
                              </span>
                            );
                          }
                          const balance =
                            walletData.CURRENT_BALENCE ||
                            walletData.CURRENT_BALANCE ||
                            walletData.BALANCE ||
                            walletData.AMOUNT ||
                            0;
                          const balanceNum =
                            typeof balance === "string"
                              ? parseFloat(balance.replace(/,/g, ""))
                              : parseFloat(balance);
                          const formattedBalance = Number.isNaN(balanceNum) ? 0 : balanceNum;
                          const {
                            flagCode: flagCountryCode,
                            code: currencyCode,
                            sym: currencySymbol,
                            suffix,
                          } = resolveCashWalletCurrencyDisplay(
                            walletData,
                            typeof option === "object" ? option.label : ""
                          );
                          return (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <WalletCurrencyFlag
                                countryCode={flagCountryCode}
                                title={currencyCode || currencySymbol || flagCountryCode || ""}
                              />
                              <span>
                                {formattedBalance.toFixed(2)}
                                {suffix ? ` ${suffix}` : ""}
                              </span>
                            </div>
                          );
                        }}
                      />
                      {errors.wallet && (
                        <div className="cash-in-error-message">{errors.wallet}</div>
                      )}
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">
                        {t("amount_to_add", { ns: "mobile" })}
                      </label>
                      <div className="cash-in-input-wrapper">
                        <span className="cash-in-input-icon">
                          <i className="fa-solid fa-credit-card"></i>
                        </span>
                        <CustomInput
                          type="text"
                          placeholder={t("enter_amount_to_add", {
                            ns: "mobile",
                          })}
                          value={amount}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^\d.,]/g, "");
                            setAmount(v);
                            if (errors.amount) setErrors((p) => ({ ...p, amount: undefined }));
                          }}
                          className={`cash-in-input ${errors.amount ? "cash-in-input-error" : ""}`}
                          disabled={loading}
                        />
                      </div>
                      {errors.amount && (
                        <div className="cash-in-error-message">{errors.amount}</div>
                      )}
                    </div>

                    <div className="cash-in-actions" style={{ marginTop: 24 }}>
                      <CustomButton
                        className="cash-in-proceed-button"
                        variant="primary"
                        onClick={handleProceed}
                        disabled={loading || processing}
                      >
                        <span>
                          {processing
                            ? `${t("loading", { ns: "mobile" })}…`
                            : t("submit", { ns: "mobile" })}
                        </span>
                      </CustomButton>
                    </div>
                  </div>
                </div>

                <div className="cash-in-side-panel">
                  <div className="cash-in-info-card">
                    <h3>
                      <i className="fa-solid fa-credit-card" />
                      {t("pay_with_card", { ns: "mobile" })}
                    </h3>
                    <ul>
                      <li>
                        <span>{t("wallet", { ns: "mobile" })}</span>
                        <strong>{walletSymbol || "—"}</strong>
                      </li>
                      <li>
                        <span>{t("transaction_type", { ns: "mobile" })}</span>
                        <strong>{t("pay_with_card", { ns: "mobile" })}</strong>
                      </li>
                    </ul>
                  </div>

                  <div className="cash-in-help-card">
                    <h3>
                      <i className="fa-solid fa-headset" />
                      {t("contactus_title", { ns: "mobile" })}
                    </h3>
                    <p>{t("cash_in_help_description", { ns: "mobile" })}</p>
                    <button
                      type="button"
                      onClick={() =>
                        openSupportChatOnCurrentPage(
                          navigate,
                          location.pathname,
                          location.search
                        )
                      }
                    >
                      {t("contact_us", { ns: "mobile" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <TransactionPasswordDialog
        open={passwordDialogOpen && !!passwordDialogData}
        onClose={handlePasswordDialogClose}
        mode="pay_by_card"
        requestToken={passwordDialogData?.requestToken ?? ""}
        firstMessage={passwordDialogData?.firstMessage ?? ""}
        secondMessage={passwordDialogData?.secondMessage ?? ""}
      />
    </div>
  );
}
